import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Play, 
  Loader2, 
  Link2, 
  CheckCircle, 
  XCircle,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Database,
  Save,
  Bug,
  Copy
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Separator } from '@/components/ui/separator'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useToast } from '@/hooks/use-toast'
import {
  useIntegrations,
  useCreateIntegration,
  useUpdateIntegration,
  useDeleteIntegration,
  useToggleIntegration,
  useTestIntegration,
  useValidateIntents,
  Integration,
  CreateIntegrationData,
  IntentConflict,
  ValidationRoute,
} from '@/hooks/useIntegrations'
import { useAssistant, useUpdateAssistant } from '@/hooks/useAssistants'

interface IntegrationsTabProps {
  assistantId: string
}

type ViewMode = 'list' | 'add' | 'edit'

interface FormData {
  integration_type: 'openai_compatible' | 'generic_api' | 'crawler'
  name: string
  endpoint_url: string
  api_key: string
  auth_header_type: 'bearer' | 'x-api-key' | 'api-key' | 'custom'
  custom_auth_header_name: string
  is_openai_compatible: boolean
  response_json_path: string
  description: string
  routing_examples: string
  requires_session: boolean
  session_key_name: string
  session_end_strategy: 'response_field' | 'timeout' | 'keyword' | 'manual'
  session_timeout_seconds: number
  session_end_keywords: string
  session_end_response_field: string
  custom_headers: string
  crawler_include_domains: string
  crawler_exclude_domains: string
  crawler_max_results: number
  is_active: boolean
}

const initialFormData: FormData = {
  integration_type: 'openai_compatible',
  name: '',
  endpoint_url: '',
  api_key: '',
  auth_header_type: 'bearer',
  custom_auth_header_name: '',
  is_openai_compatible: true,
  response_json_path: '',
  description: '',
  routing_examples: '',
  requires_session: false,
  session_key_name: 'user',
  session_end_strategy: 'response_field',
  session_timeout_seconds: 300,
  session_end_keywords: '',
  session_end_response_field: 'session_complete',
  custom_headers: '',
  crawler_include_domains: '',
  crawler_exclude_domains: '',
  crawler_max_results: 5,
  is_active: true,
}

export function IntegrationsTab({ assistantId }: IntegrationsTabProps) {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { data: integrations, isLoading } = useIntegrations(assistantId)
  const { data: assistant } = useAssistant(assistantId)
  const updateAssistant = useUpdateAssistant()
  
  const createIntegration = useCreateIntegration()
  const updateIntegration = useUpdateIntegration()
  const deleteIntegration = useDeleteIntegration()
  const toggleIntegration = useToggleIntegration()
  const testIntegration = useTestIntegration()

  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [editingIntegration, setEditingIntegration] = useState<Integration | null>(null)
  const [formData, setFormData] = useState<FormData>(initialFormData)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [testResult, setTestResult] = useState<{ status: 'success' | 'error'; message: string } | null>(null)
  const [testingId, setTestingId] = useState<number | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  
  // KB Routing Examples state
  const [kbRoutingExamples, setKbRoutingExamples] = useState('')
  const [kbExamplesSaving, setKbExamplesSaving] = useState(false)
  
  // Debug section state
  const [debugOpen, setDebugOpen] = useState(false)
  
  // Intent validation state
  const [conflicts, setConflicts] = useState<IntentConflict[]>([])
  const [validationRun, setValidationRun] = useState(false)
  const validateIntents = useValidateIntents()
  
  // Generate intent classifier prompt preview
  const generatePromptPreview = () => {
    const routeDescriptions: string[] = []
    
    // Add KB routing examples if available
    const kbExamples = kbRoutingExamples.split('\n').filter(ex => ex.trim())
    if (kbExamples.length > 0) {
      const kbExamplesFormatted = kbExamples.map(ex => `    • ${ex.trim()}`).join('\n')
      routeDescriptions.push(`  - ID: ragflow
    Name: Knowledge Base
    Description: Internal documents and knowledge base content
    Examples:
${kbExamplesFormatted}`)
    }
    
    // Add external integrations
    if (integrations && integrations.length > 0) {
      for (const integration of integrations.filter(i => i.is_active)) {
        const examples = integration.routing_examples?.split('\n').filter(ex => ex.trim()) || []
        const examplesFormatted = examples.map(ex => `    • ${ex.trim()}`).join('\n')
        routeDescriptions.push(`  - ID: ${integration.id}
    Name: ${integration.name}
    Description: ${integration.description || 'No description provided'}
    Examples:
${examplesFormatted}`)
      }
    }
    
    if (routeDescriptions.length === 0) {
      return 'No routing options configured. Add KB routing examples or external integrations to enable intent classification.'
    }
    
    const routesText = routeDescriptions.join('\n\n')
    
    return `Available routes:

${routesText}

User message: "{user's message here}"

Analyze the user's message and determine the best route:
- If the message clearly matches a route's examples or purpose, return that route's ID
- If the message is about internal documents/knowledge base topics, return "ragflow"
- If uncertain, return "ragflow" (the knowledge base will be searched first)

Return ONLY the route ID (e.g., "ragflow" or a number like "7").`
  }
  
  // Build routes for validation
  const buildValidationRoutes = (): ValidationRoute[] => {
    const routes: ValidationRoute[] = []
    
    // Add KB routing examples
    const kbExamples = kbRoutingExamples.split('\n').filter(ex => ex.trim())
    if (kbExamples.length > 0) {
      routes.push({
        id: 'ragflow',
        name: 'Knowledge Base',
        examples: kbExamples.map(ex => ex.trim())
      })
    }
    
    // Add external integrations
    if (integrations && integrations.length > 0) {
      for (const integration of integrations.filter(i => i.is_active)) {
        const examples = integration.routing_examples?.split('\n').filter(ex => ex.trim()) || []
        if (examples.length > 0) {
          routes.push({
            id: String(integration.id),
            name: integration.name,
            examples: examples.map(ex => ex.trim())
          })
        }
      }
    }
    
    return routes
  }
  
  // Handle validate intents
  const handleValidateIntents = async () => {
    const routes = buildValidationRoutes()
    
    if (routes.length < 2) {
      toast({
        title: 'Cannot Validate',
        description: 'Need at least 2 routes with examples to check for overlaps',
        variant: 'destructive',
      })
      return
    }
    
    try {
      const result = await validateIntents.mutateAsync({ assistantId, routes })
      setConflicts(result.conflicts || [])
      setValidationRun(true)
      
      if (result.conflicts && result.conflicts.length > 0) {
        toast({
          title: 'Conflicts Found',
          description: `Found ${result.conflicts.length} semantic overlap(s)`,
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'No Conflicts',
          description: 'All intent examples are distinct',
        })
      }
    } catch (error: any) {
      toast({
        title: 'Validation Failed',
        description: error.response?.data?.error || 'Failed to validate intents',
        variant: 'destructive',
      })
    }
  }
  
  // Sync KB routing examples from assistant data
  useEffect(() => {
    if (assistant?.kb_routing_examples !== undefined) {
      setKbRoutingExamples(assistant.kb_routing_examples || '')
    }
  }, [assistant?.kb_routing_examples])
  
  // Save KB routing examples
  const handleSaveKbExamples = async () => {
    setKbExamplesSaving(true)
    try {
      await updateAssistant.mutateAsync({
        assistantId,
        data: { kb_routing_examples: kbRoutingExamples }
      })
      toast({
        title: 'Saved',
        description: 'Knowledge base routing examples updated successfully.',
      })
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to save routing examples',
        variant: 'destructive',
      })
    } finally {
      setKbExamplesSaving(false)
    }
  }

  // Reset form and switch to list view
  const resetForm = () => {
    setFormData(initialFormData)
    setFormErrors({})
    setTestResult(null)
    setEditingIntegration(null)
    setViewMode('list')
    setAdvancedOpen(false)
  }

  // Open add form - navigate to wizard
  const handleAddClick = () => {
    navigate(`/app/assistants/${assistantId}/integrations/wizard`)
  }

  // Open edit form - navigate to wizard with integration ID
  const handleEditClick = (integration: Integration) => {
    navigate(`/app/assistants/${assistantId}/integrations/wizard?id=${integration.id}`)
  }


  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}

    // Name required only for non-crawler types
    if (formData.integration_type !== 'crawler' && !formData.name.trim()) {
      errors.name = 'Name is required'
    }

    // Type-specific validation
    if (formData.integration_type === 'crawler') {
      // Crawler only needs to be active, domain filters are optional
      // No other validation needed for crawler
    } else {
      // API integrations need endpoint_url and api_key
      if (!formData.endpoint_url.trim()) {
        errors.endpoint_url = 'Endpoint URL is required'
      } else {
        try {
          new URL(formData.endpoint_url)
        } catch {
          errors.endpoint_url = 'Enter a valid URL'
        }
      }

      // API key required only for new integrations
      if (viewMode === 'add' && !formData.api_key.trim()) {
        errors.api_key = 'API key is required'
      }

      // Routing examples required for API integrations
      if (!formData.routing_examples.trim()) {
        errors.routing_examples = 'At least one routing example is required'
      }
    }

    if (formData.requires_session && !formData.session_key_name.trim()) {
      errors.session_key_name = 'Session key name is required when session tracking is enabled'
    }

    if (formData.integration_type !== 'crawler' && formData.auth_header_type === 'custom' && !formData.custom_auth_header_name.trim()) {
      errors.custom_auth_header_name = 'Custom header name is required when auth type is custom'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return

    try {
      if (viewMode === 'add') {
        const data: CreateIntegrationData = {
          integration_type: formData.integration_type,
          name: formData.integration_type === 'crawler' ? 'Web Crawler' : formData.name.trim(),
          endpoint_url: formData.integration_type !== 'crawler' ? formData.endpoint_url.trim() : '',
          api_key: formData.integration_type !== 'crawler' ? formData.api_key.trim() : '',
          auth_header_type: formData.integration_type !== 'crawler' ? formData.auth_header_type : 'bearer',
          custom_auth_header_name: formData.auth_header_type === 'custom' ? formData.custom_auth_header_name.trim() : '',
          is_openai_compatible: formData.integration_type === 'openai_compatible',
          response_json_path: formData.integration_type === 'generic_api' ? formData.response_json_path.trim() : '',
          description: formData.integration_type === 'crawler' ? 'Web crawler for real-time search' : formData.description.trim(),
          routing_examples: formData.integration_type === 'crawler' ? 'search\nweb search\nfind information' : formData.routing_examples.trim(),
          requires_session: formData.integration_type !== 'crawler' ? formData.requires_session : false,
          session_key_name: formData.requires_session ? formData.session_key_name.trim() : '',
          session_end_strategy: formData.requires_session ? formData.session_end_strategy : 'response_field',
          session_timeout_seconds: formData.requires_session ? formData.session_timeout_seconds : 300,
          session_end_keywords: formData.requires_session && formData.session_end_strategy === 'keyword' ? formData.session_end_keywords.trim() : '',
          session_end_response_field: formData.requires_session && formData.session_end_strategy === 'response_field' ? formData.session_end_response_field.trim() : 'session_complete',
          custom_headers: formData.integration_type !== 'crawler' ? formData.custom_headers.trim() : '',
          crawler_include_domains: formData.integration_type === 'crawler' ? formData.crawler_include_domains.trim() : '',
          crawler_exclude_domains: formData.integration_type === 'crawler' ? formData.crawler_exclude_domains.trim() : '',
          crawler_max_results: formData.integration_type === 'crawler' ? formData.crawler_max_results : 5,
          is_active: formData.is_active,
        }

        await createIntegration.mutateAsync({ assistantId, data })
        toast({
          title: 'Integration created',
          description: `"${formData.name}" has been created successfully.`,
        })
      } else if (viewMode === 'edit' && editingIntegration) {
        const data: Record<string, any> = {
          name: formData.name.trim(),
          endpoint_url: formData.integration_type !== 'crawler' ? formData.endpoint_url.trim() : '',
          auth_header_type: formData.integration_type !== 'crawler' ? formData.auth_header_type : 'bearer',
          custom_auth_header_name: formData.auth_header_type === 'custom' ? formData.custom_auth_header_name.trim() : '',
          is_openai_compatible: formData.integration_type === 'openai_compatible',
          response_json_path: formData.integration_type === 'generic_api' ? formData.response_json_path.trim() : '',
          description: formData.description.trim(),
          routing_examples: formData.routing_examples.trim(),
          requires_session: formData.integration_type !== 'crawler' ? formData.requires_session : false,
          session_key_name: formData.requires_session ? formData.session_key_name.trim() : '',
          session_end_strategy: formData.requires_session ? formData.session_end_strategy : 'response_field',
          session_timeout_seconds: formData.requires_session ? formData.session_timeout_seconds : 300,
          session_end_keywords: formData.requires_session && formData.session_end_strategy === 'keyword' ? formData.session_end_keywords.trim() : '',
          session_end_response_field: formData.requires_session && formData.session_end_strategy === 'response_field' ? formData.session_end_response_field.trim() : 'session_complete',
          custom_headers: formData.integration_type !== 'crawler' ? formData.custom_headers.trim() : '',
          crawler_include_domains: formData.integration_type === 'crawler' ? formData.crawler_include_domains.trim() : '',
          crawler_exclude_domains: formData.integration_type === 'crawler' ? formData.crawler_exclude_domains.trim() : '',
          crawler_max_results: formData.integration_type === 'crawler' ? formData.crawler_max_results : 5,
          is_active: formData.is_active,
        }

        // Only include API key if user entered a new one
        if (formData.api_key.trim()) {
          data.api_key = formData.api_key.trim()
        }

        await updateIntegration.mutateAsync({ integrationId: editingIntegration.id, data })
        toast({
          title: 'Integration updated',
          description: `"${formData.name}" has been updated successfully.`,
        })
      }

      resetForm()
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'An error occurred'
      const fieldErrors = error.response?.data?.errors
      
      if (fieldErrors) {
        setFormErrors(fieldErrors)
      }
      
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Handle toggle active status
  const handleToggle = async (integration: Integration, checked: boolean) => {
    try {
      await toggleIntegration.mutateAsync({ integrationId: integration.id, isActive: checked })
      toast({
        title: checked ? 'Integration activated' : 'Integration deactivated',
        description: `"${integration.name}" is now ${checked ? 'active' : 'inactive'}.`,
      })
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to toggle integration',
        variant: 'destructive',
      })
    }
  }

  // Handle test connection (from form)
  const handleTestFromForm = async () => {
    if (!editingIntegration) return
    
    setTestResult(null)
    setTestingId(editingIntegration.id)
    
    try {
      const result = await testIntegration.mutateAsync(editingIntegration.id)
      setTestResult(result)
    } catch (error: any) {
      setTestResult({
        status: 'error',
        message: error.response?.data?.message || 'Test failed',
      })
    } finally {
      setTestingId(null)
    }
  }

  // Handle test connection (from table)
  const handleTestFromTable = async (integration: Integration) => {
    setTestingId(integration.id)
    
    try {
      const result = await testIntegration.mutateAsync(integration.id)
      toast({
        title: result.status === 'success' ? 'Connection successful' : 'Connection failed',
        description: result.message,
        variant: result.status === 'success' ? 'default' : 'destructive',
      })
    } catch (error: any) {
      toast({
        title: 'Test failed',
        description: error.response?.data?.message || 'Failed to test connection',
        variant: 'destructive',
      })
    } finally {
      setTestingId(null)
    }
  }

  // Handle delete
  const handleDelete = async (integration: Integration) => {
    try {
      await deleteIntegration.mutateAsync(integration.id)
      setDeletingId(null)
      toast({
        title: 'Integration deleted',
        description: `"${integration.name}" has been deleted.`,
      })
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to delete integration',
        variant: 'destructive',
      })
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Routing Configuration</h2>
          <p className="text-muted-foreground">Configure how queries are routed to your knowledge base and external services</p>
        </div>
      </div>

      {/* Add/Edit Form */}
      {(viewMode === 'add' || viewMode === 'edit') && (
        <Card>
          <CardHeader>
            <CardTitle>{viewMode === 'add' ? 'Add New Integration' : 'Edit Integration'}</CardTitle>
            <CardDescription>
              {viewMode === 'add' 
                ? 'Configure a new external API endpoint for routing'
                : `Editing "${editingIntegration?.name}"`
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* ================================================================
                SECTION 0: INTEGRATION TYPE (only show for new integrations)
                ================================================================ */}
            {viewMode === 'add' && (
              <>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Integration Type</h3>
                    <p className="text-xs text-muted-foreground">
                      Choose the type of integration you want to create.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <label 
                      className={`flex items-start space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                        formData.integration_type === 'openai_compatible' 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border hover:border-primary/50'
                      }`}
                      onClick={() => setFormData({ ...formData, integration_type: 'openai_compatible' })}
                    >
                      <input
                        type="radio"
                        checked={formData.integration_type === 'openai_compatible'}
                        onChange={() => setFormData({ ...formData, integration_type: 'openai_compatible' })}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium">OpenAI Compatible Endpoint</div>
                        <div className="text-xs text-muted-foreground">
                          Standard OpenAI chat completions API format
                        </div>
                      </div>
                    </label>

                    <label 
                      className={`flex items-start space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                        formData.integration_type === 'generic_api' 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border hover:border-primary/50'
                      }`}
                      onClick={() => setFormData({ ...formData, integration_type: 'generic_api' })}
                    >
                      <input
                        type="radio"
                        checked={formData.integration_type === 'generic_api'}
                        onChange={() => setFormData({ ...formData, integration_type: 'generic_api' })}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium">Generic Conversation Endpoint</div>
                        <div className="text-xs text-muted-foreground">
                          Custom API with flexible response parsing
                        </div>
                      </div>
                    </label>

                    <label 
                      className={`flex items-start space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                        formData.integration_type === 'crawler' 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border hover:border-primary/50'
                      }`}
                      onClick={() => setFormData({ ...formData, integration_type: 'crawler' })}
                    >
                      <input
                        type="radio"
                        checked={formData.integration_type === 'crawler'}
                        onChange={() => setFormData({ ...formData, integration_type: 'crawler' })}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium">Web Crawler</div>
                        <div className="text-xs text-muted-foreground">
                          Real-time web search for current information
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
                <Separator />
              </>
            )}

            {/* ================================================================
                SECTION 1: BASIC INFORMATION
                ================================================================ */}
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  {formData.integration_type === 'crawler' ? 'Crawler Configuration' : 'Basic Information'}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {formData.integration_type === 'crawler' 
                    ? 'Configure the web crawler settings and status.'
                    : 'Define the integration\'s name, description, and active status.'}
                </p>
              </div>

              {/* Integration Name - Hidden for crawler */}
              {formData.integration_type !== 'crawler' && (
                <div className="space-y-2">
                  <Label htmlFor="name">Integration Name *</Label>
                  <Input
                    id="name"
                    placeholder="e.g., Municipal Service Requests"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={formErrors.name ? 'border-destructive' : ''}
                  />
                  <p className="text-xs text-muted-foreground">
                    A descriptive name shown in the routing list and logs. Choose something that clearly identifies the external service.
                  </p>
                  {formErrors.name && (
                    <p className="text-sm text-destructive">{formErrors.name}</p>
                  )}
                </div>
              )}

              {/* Description - Hidden for crawler */}
              {formData.integration_type !== 'crawler' && (
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    rows={2}
                    placeholder="Handles citizen service requests like potholes, fallen trees, etc."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">
                    Brief description to help the AI understand when to route queries to this integration. This is used during intent classification.
                  </p>
                </div>
              )}

              {/* Active Status */}
              <div className="flex items-start space-x-3 p-3 rounded-md border bg-muted/30">
                <Checkbox
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => 
                    setFormData({ ...formData, is_active: checked as boolean })
                  }
                  className="mt-0.5"
                />
                <div className="space-y-1">
                  <Label htmlFor="is_active" className="cursor-pointer font-medium">
                    Active
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    When enabled, this integration will be considered for routing. Disable to temporarily exclude it without deleting.
                  </p>
                </div>
              </div>
            </div>

            <Separator />

            {/* ================================================================
                SECTION 2: CONNECTION SETTINGS (only for API integrations)
                ================================================================ */}
            {formData.integration_type !== 'crawler' && (
              <>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Connection Settings</h3>
                    <p className="text-xs text-muted-foreground">
                      Configure how to connect and authenticate with the external API.
                    </p>
                  </div>

                  {/* Endpoint URL */}
                  <div className="space-y-2">
                <Label htmlFor="endpoint_url">API Endpoint URL *</Label>
                <Input
                  id="endpoint_url"
                  type="url"
                  placeholder="https://api.example.com/v1/chat/completions"
                  value={formData.endpoint_url}
                  onChange={(e) => setFormData({ ...formData, endpoint_url: e.target.value })}
                  className={formErrors.endpoint_url ? 'border-destructive' : ''}
                />
                <p className="text-xs text-muted-foreground">
                  The full URL of the external API endpoint. This should accept POST requests with a chat-style payload.
                </p>
                {formErrors.endpoint_url && (
                  <p className="text-sm text-destructive">{formErrors.endpoint_url}</p>
                )}
              </div>

              {/* API Key */}
              <div className="space-y-2">
                <Label htmlFor="api_key">API Key {viewMode === 'add' ? '*' : ''}</Label>
                <Input
                  id="api_key"
                  type="password"
                  placeholder={viewMode === 'edit' ? '••••••••' : 'Enter API key'}
                  value={formData.api_key}
                  onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                  className={formErrors.api_key ? 'border-destructive' : ''}
                />
                <p className="text-xs text-muted-foreground">
                  {viewMode === 'edit' 
                    ? 'Leave blank to keep the existing key. Your API key is stored encrypted and never exposed.'
                    : 'Your API key will be encrypted and stored securely. It is never exposed in the UI or logs.'}
                </p>
                {formErrors.api_key && (
                  <p className="text-sm text-destructive">{formErrors.api_key}</p>
                )}
              </div>

              {/* Auth Header Type */}
              <div className="space-y-2">
                <Label htmlFor="auth_header_type">Authentication Header Type</Label>
                <Select
                  value={formData.auth_header_type}
                  onValueChange={(value: FormData['auth_header_type']) => 
                    setFormData({ ...formData, auth_header_type: value })
                  }
                >
                  <SelectTrigger className={formErrors.auth_header_type ? 'border-destructive' : ''}>
                    <SelectValue placeholder="Select auth header type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bearer">Bearer Token (Authorization: Bearer)</SelectItem>
                    <SelectItem value="x-api-key">X-API-Key (x-api-key)</SelectItem>
                    <SelectItem value="api-key">API-Key (Api-Key)</SelectItem>
                    <SelectItem value="custom">Custom Header</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Specifies how the API key is sent in request headers. Most APIs use Bearer Token or X-API-Key.
                </p>
                {formErrors.auth_header_type && (
                  <p className="text-sm text-destructive">{formErrors.auth_header_type}</p>
                )}
              </div>

              {/* Custom Auth Header Name */}
              {formData.auth_header_type === 'custom' && (
                <div className="space-y-2 ml-4 pl-4 border-l-2 border-muted">
                  <Label htmlFor="custom_auth_header_name">Custom Header Name *</Label>
                  <Input
                    id="custom_auth_header_name"
                    placeholder="X-Auth-Token"
                    value={formData.custom_auth_header_name}
                    onChange={(e) => setFormData({ ...formData, custom_auth_header_name: e.target.value })}
                    className={formErrors.custom_auth_header_name ? 'border-destructive' : ''}
                  />
                  <p className="text-xs text-muted-foreground">
                    The exact header name your API expects (e.g., X-Auth-Token, Authorization-Key).
                  </p>
                  {formErrors.custom_auth_header_name && (
                    <p className="text-sm text-destructive">{formErrors.custom_auth_header_name}</p>
                  )}
                </div>
              )}

              {/* Custom Headers - Collapsible */}
              <Collapsible>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center gap-2 p-0 h-auto text-muted-foreground hover:text-foreground">
                    <ChevronDown className="h-3 w-3" />
                    <span className="text-xs">Additional Headers (optional)</span>
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-3">
                  <div className="space-y-2">
                    <Textarea
                      id="custom_headers"
                      rows={3}
                      placeholder={`X-Custom-Header:value\nX-API-Version:2.0`}
                      value={formData.custom_headers}
                      onChange={(e) => setFormData({ ...formData, custom_headers: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Extra headers to include with every request. One per line in key:value format.
                    </p>
                  </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>

              <Separator />
            </>
            )}

            {/* ================================================================
                SECTION 2B: CRAWLER SETTINGS (only for crawler integrations)
                ================================================================ */}
            {formData.integration_type === 'crawler' && (
              <>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Crawler Settings</h3>
                    <p className="text-xs text-muted-foreground">
                      Configure domain filters and search result limits for web crawling.
                    </p>
                  </div>

                  {/* Include Domains */}
                  <div className="space-y-2">
                    <Label htmlFor="crawler_include_domains">Include Domains</Label>
                    <Input
                      id="crawler_include_domains"
                      placeholder="example.com, news.example.org"
                      value={formData.crawler_include_domains}
                      onChange={(e) => setFormData({ ...formData, crawler_include_domains: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Comma-separated list of domains to search. Leave empty to search all domains.
                    </p>
                  </div>

                  {/* Exclude Domains */}
                  <div className="space-y-2">
                    <Label htmlFor="crawler_exclude_domains">Exclude Domains</Label>
                    <Input
                      id="crawler_exclude_domains"
                      placeholder="ads.example.com, spam.example.org"
                      value={formData.crawler_exclude_domains}
                      onChange={(e) => setFormData({ ...formData, crawler_exclude_domains: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Comma-separated list of domains to exclude from search results.
                    </p>
                  </div>

                  {/* Max Results */}
                  <div className="space-y-2">
                    <Label htmlFor="crawler_max_results">Maximum Results</Label>
                    <Input
                      id="crawler_max_results"
                      type="number"
                      min="1"
                      max="10"
                      value={formData.crawler_max_results}
                      onChange={(e) => setFormData({ ...formData, crawler_max_results: parseInt(e.target.value) || 5 })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Maximum number of search results to return (1-10).
                    </p>
                  </div>

                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      Web crawler uses your tenant's API key configured in system settings. Contact support if web search is not working.
                    </AlertDescription>
                  </Alert>
                </div>

                <Separator />
              </>
            )}

            {/* ================================================================
                SECTION 3: INTENT ROUTING (hidden for crawler)
                ================================================================ */}
            {formData.integration_type !== 'crawler' && (
              <>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Intent Routing</h3>
                    <p className="text-xs text-muted-foreground">
                      Define when user queries should be routed to this integration instead of the knowledge base.
                    </p>
                  </div>

                  {/* Routing Examples */}
                  <div className="space-y-2">
                <Label htmlFor="routing_examples">Routing Examples *</Label>
                <Textarea
                  id="routing_examples"
                  rows={5}
                  placeholder={`Enter one example per line:
tree fell on my street
report a pothole on Main St
check status of request PW-12345
I want to file a complaint`}
                  value={formData.routing_examples}
                  onChange={(e) => setFormData({ ...formData, routing_examples: e.target.value })}
                  className={formErrors.routing_examples ? 'border-destructive' : ''}
                />
                <p className="text-xs text-muted-foreground">
                  Provide example user messages that should trigger this integration. The AI uses these examples to learn 
                  which types of queries to route here. More diverse examples = better routing accuracy.
                </p>
                {formErrors.routing_examples && (
                  <p className="text-sm text-destructive">{formErrors.routing_examples}</p>
                )}
                  </div>
                </div>

                <Separator />
              </>
            )}

            {/* ================================================================
                SECTION 4: SESSION MANAGEMENT (Collapsible, hidden for crawler)
                ================================================================ */}
            {formData.integration_type !== 'crawler' && (
            <Collapsible>
              <CollapsibleTrigger asChild>
                <div className="flex items-center justify-between cursor-pointer group">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground group-hover:text-primary">Session Management</h3>
                    <p className="text-xs text-muted-foreground">
                      Configure multi-turn conversation tracking with the external API.
                    </p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-transform" />
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-4 space-y-4">
                {/* Requires Session Toggle */}
                <div className="flex items-start space-x-3 p-3 rounded-md border bg-muted/30">
                  <Checkbox
                    id="requires_session"
                    checked={formData.requires_session}
                    onCheckedChange={(checked) => 
                      setFormData({ ...formData, requires_session: checked as boolean })
                    }
                    className="mt-0.5"
                  />
                  <div className="space-y-1">
                    <Label htmlFor="requires_session" className="cursor-pointer font-medium">
                      Enable Session Tracking
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      When enabled, the system maintains conversation context across multiple messages. 
                      Use this for APIs that support multi-turn conversations (e.g., service request workflows).
                    </p>
                  </div>
                </div>

                {formData.requires_session && (
                  <div className="space-y-4 ml-4 pl-4 border-l-2 border-muted">
                    {/* Session Key Name */}
                    <div className="space-y-2">
                      <Label htmlFor="session_key_name">Session Key Name</Label>
                      <Input
                        id="session_key_name"
                        placeholder="user"
                        value={formData.session_key_name}
                        onChange={(e) => setFormData({ ...formData, session_key_name: e.target.value })}
                        className={formErrors.session_key_name ? 'border-destructive' : ''}
                      />
                      <p className="text-xs text-muted-foreground">
                        The JSON field name used to pass the session ID to the API (e.g., 'user', 'session_id', 'conversation_id').
                      </p>
                      {formErrors.session_key_name && (
                        <p className="text-sm text-destructive">{formErrors.session_key_name}</p>
                      )}
                    </div>

                    {/* Session End Strategy */}
                    <div className="space-y-2">
                      <Label htmlFor="session_end_strategy">Session End Strategy</Label>
                      <Select
                        value={formData.session_end_strategy}
                        onValueChange={(value: FormData['session_end_strategy']) => 
                          setFormData({ ...formData, session_end_strategy: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select strategy" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="response_field">Response Field</SelectItem>
                          <SelectItem value="timeout">Timeout</SelectItem>
                          <SelectItem value="keyword">Keyword Match</SelectItem>
                          <SelectItem value="manual">Manual</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        {formData.session_end_strategy === 'response_field' && 
                          'Session ends when the API returns a specific field (e.g., session_complete: true).'}
                        {formData.session_end_strategy === 'timeout' && 
                          'Session ends automatically after a period of inactivity.'}
                        {formData.session_end_strategy === 'keyword' && 
                          'Session ends when the response contains specific keywords (e.g., "goodbye", "thank you").'}
                        {formData.session_end_strategy === 'manual' && 
                          'Session never auto-ends. User must explicitly start a new conversation.'}
                      </p>
                    </div>

                    {/* Session Timeout */}
                    <div className="space-y-2">
                      <Label htmlFor="session_timeout_seconds">
                        {formData.session_end_strategy === 'timeout' ? 'Inactivity Timeout' : 'Fallback Timeout'} (seconds)
                      </Label>
                      <Input
                        id="session_timeout_seconds"
                        type="number"
                        min={30}
                        max={3600}
                        placeholder="300"
                        value={formData.session_timeout_seconds}
                        onChange={(e) => setFormData({ ...formData, session_timeout_seconds: parseInt(e.target.value) || 300 })}
                      />
                      <p className="text-xs text-muted-foreground">
                        {formData.session_end_strategy === 'timeout' 
                          ? 'Session ends after this many seconds without user activity.'
                          : 'Safety timeout: session ends if the primary strategy doesn\'t trigger within this time. Default: 300s (5 min).'}
                      </p>
                    </div>

                    {/* Response Field Name */}
                    {formData.session_end_strategy === 'response_field' && (
                      <div className="space-y-2">
                        <Label htmlFor="session_end_response_field">Completion Field Name</Label>
                        <Input
                          id="session_end_response_field"
                          placeholder="session_complete"
                          value={formData.session_end_response_field}
                          onChange={(e) => setFormData({ ...formData, session_end_response_field: e.target.value })}
                        />
                        <p className="text-xs text-muted-foreground">
                          The JSON field in the API response that indicates session completion (e.g., 'session_complete', 'x_completed', 'done').
                        </p>
                      </div>
                    )}

                    {/* End Keywords */}
                    {formData.session_end_strategy === 'keyword' && (
                      <div className="space-y-2">
                        <Label htmlFor="session_end_keywords">End Keywords</Label>
                        <Input
                          id="session_end_keywords"
                          placeholder="goodbye, thank you, bye, done, completed"
                          value={formData.session_end_keywords}
                          onChange={(e) => setFormData({ ...formData, session_end_keywords: e.target.value })}
                        />
                        <p className="text-xs text-muted-foreground">
                          Comma-separated list of keywords. If any appear in the response, the session ends. Case-insensitive.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
            )}

            {/* ================================================================
                SECTION 5: RESPONSE FORMAT (hidden for crawler)
                ================================================================ */}
            {formData.integration_type !== 'crawler' && (
              <>
                <Separator />
                <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
              <CollapsibleTrigger asChild>
                <div className="flex items-center justify-between cursor-pointer group">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground group-hover:text-primary">Response Format</h3>
                    <p className="text-xs text-muted-foreground">
                      Configure how to extract the response message from the API.
                    </p>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-muted-foreground group-hover:text-primary transition-transform ${advancedOpen ? 'rotate-180' : ''}`} />
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-4 space-y-4">
                {/* OpenAI Compatible Toggle */}
                <div className="flex items-start space-x-3 p-3 rounded-md border bg-muted/30">
                  <Checkbox
                    id="is_openai_compatible"
                    checked={formData.is_openai_compatible}
                    onCheckedChange={(checked) => 
                      setFormData({ ...formData, is_openai_compatible: checked as boolean })
                    }
                    className="mt-0.5"
                  />
                  <div className="space-y-1">
                    <Label htmlFor="is_openai_compatible" className="cursor-pointer font-medium">
                      OpenAI-Compatible Response Format
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Enable if the API returns responses in OpenAI format: <code className="bg-muted px-1 rounded">{`{"choices":[{"message":{"content":"..."}}]}`}</code>
                    </p>
                  </div>
                </div>

                {/* Response JSON Path */}
                {!formData.is_openai_compatible && (
                  <div className="space-y-2 ml-4 pl-4 border-l-2 border-muted">
                    <Label htmlFor="response_json_path">Response JSON Path</Label>
                    <Input
                      id="response_json_path"
                      placeholder="response or data.answer or result.message"
                      value={formData.response_json_path}
                      onChange={(e) => setFormData({ ...formData, response_json_path: e.target.value })}
                      className={formErrors.response_json_path ? 'border-destructive' : ''}
                    />
                    <p className="text-xs text-muted-foreground">
                      Dot-notation path to extract the message from the JSON response. Examples: 
                      <code className="bg-muted px-1 rounded ml-1">response</code>, 
                      <code className="bg-muted px-1 rounded ml-1">data.answer</code>, 
                      <code className="bg-muted px-1 rounded ml-1">result.0.text</code>
                    </p>
                    {formErrors.response_json_path && (
                      <p className="text-sm text-destructive">{formErrors.response_json_path}</p>
                    )}
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>

            {/* Test Result */}
            {testResult && (
              <Alert variant={testResult.status === 'success' ? 'default' : 'destructive'}>
                {testResult.status === 'success' ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <XCircle className="h-4 w-4" />
                )}
                <AlertDescription>{testResult.message}</AlertDescription>
              </Alert>
            )}
              </>
            )}

            {/* Form Actions */}
            <div className="flex items-center justify-between pt-4 border-t">
              <div>
                {viewMode === 'edit' && editingIntegration && (
                  <Button
                    variant="outline"
                    onClick={handleTestFromForm}
                    disabled={testingId === editingIntegration.id}
                  >
                    {testingId === editingIntegration.id ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Testing...
                      </>
                    ) : (
                      <>
                        <Play className="h-4 w-4 mr-2" />
                        Test Connection
                      </>
                    )}
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSubmit}
                  disabled={createIntegration.isPending || updateIntegration.isPending}
                >
                  {(createIntegration.isPending || updateIntegration.isPending) ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Integration List */}
      {viewMode === 'list' && (
        <>
          {/* KB Routing Examples Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Database className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Knowledge Base Routing</CardTitle>
              </div>
              <CardDescription>
                Define example queries that your knowledge base can answer. This helps the AI route questions 
                to your documents instead of external integrations.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="kb-examples">Example Queries (one per line)</Label>
                <Textarea
                  id="kb-examples"
                  rows={5}
                  placeholder="What are the park hours?&#10;Swimming pool schedule&#10;Fence permit requirements&#10;Building permit application process"
                  value={kbRoutingExamples}
                  onChange={(e) => setKbRoutingExamples(e.target.value)}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Add 5-10 example questions that represent topics covered in your connected knowledge bases. 
                  The AI will use these to decide when to search your documents vs. calling external services.
                </p>
              </div>
              <div className="flex justify-end">
                <Button 
                  onClick={handleSaveKbExamples}
                  disabled={kbExamplesSaving}
                  size="sm"
                >
                  {kbExamplesSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Examples
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* External Integrations Section Header */}
          <div className="flex items-center justify-between pt-4">
            <div>
              <h3 className="text-lg font-semibold">External Integrations</h3>
              <p className="text-sm text-muted-foreground">Configure external APIs for intent-based routing</p>
            </div>
            <Button onClick={handleAddClick} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Integration
            </Button>
          </div>

          {!integrations || integrations.length === 0 ? (
            /* Empty State */
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Link2 className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No external integrations configured</h3>
                <p className="text-muted-foreground mb-4 text-center max-w-md text-sm">
                  Add external API integrations to route specific queries to external services.
                </p>
                <Button onClick={handleAddClick} variant="outline" size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Integration
                </Button>
              </CardContent>
            </Card>
          ) : (
            /* Integration Table */
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Endpoint</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {integrations.map((integration) => (
                      <TableRow key={integration.id}>
                        {/* Delete Confirmation Row */}
                        {deletingId === integration.id ? (
                          <TableCell colSpan={4}>
                            <Alert variant="destructive" className="my-2">
                              <AlertTriangle className="h-4 w-4" />
                              <AlertDescription className="flex items-center justify-between">
                                <span>
                                  Are you sure you want to delete "{integration.name}"? This action cannot be undone.
                                </span>
                                <div className="flex gap-2 ml-4">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setDeletingId(null)}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => handleDelete(integration)}
                                    disabled={deleteIntegration.isPending}
                                  >
                                    {deleteIntegration.isPending ? (
                                      <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Deleting...
                                      </>
                                    ) : (
                                      'Yes, Delete'
                                    )}
                                  </Button>
                                </div>
                              </AlertDescription>
                            </Alert>
                          </TableCell>
                        ) : (
                          <>
                            {/* Name */}
                            <TableCell>
                              <button
                                onClick={() => handleEditClick(integration)}
                                className="font-medium text-left hover:underline"
                              >
                                {integration.name}
                              </button>
                            </TableCell>

                            {/* Endpoint */}
                            <TableCell>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="block max-w-[300px] truncate text-muted-foreground">
                                      {integration.integration_type === 'sro'
                                        ? 'Built-in SRO'
                                        : integration.integration_type === 'crawler' || !integration.endpoint_url 
                                          ? 'Chatterpillar Managed' 
                                          : integration.endpoint_url}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{integration.integration_type === 'sro'
                                        ? `Built-in Service Request Orchestrator${integration.sro_config_name ? ` (${integration.sro_config_name})` : ''}`
                                        : integration.integration_type === 'crawler' || !integration.endpoint_url 
                                          ? 'Chatterpillar Managed Service' 
                                          : integration.endpoint_url}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>

                            {/* Status Toggle */}
                            <TableCell>
                              <Switch
                                checked={integration.is_active}
                                onCheckedChange={(checked) => handleToggle(integration, checked)}
                                disabled={toggleIntegration.isPending}
                              />
                            </TableCell>

                            {/* Actions */}
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEditClick(integration)}
                                  title="Edit"
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                {integration.integration_type !== 'sro' && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleTestFromTable(integration)}
                                    disabled={testingId === integration.id}
                                    title="Test Connection"
                                  >
                                    {testingId === integration.id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Play className="h-4 w-4" />
                                    )}
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setDeletingId(integration.id)}
                                  className="text-destructive hover:text-destructive"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Debug Section - Intent Classifier Prompt Preview */}
          <Collapsible open={debugOpen} onOpenChange={setDebugOpen} className="mt-6">
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
                {debugOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                <Bug className="h-4 w-4" />
                <span>Debug: Intent Classifier Prompt Preview</span>
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <Card className="mt-2 border-dashed border-amber-500/50 bg-amber-50/30 dark:bg-amber-950/10">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Bug className="h-4 w-4 text-amber-600" />
                      <CardTitle className="text-sm text-amber-700 dark:text-amber-400">Debug Only</CardTitle>
                    </div>
                  </div>
                  <CardDescription className="text-xs">
                    Validate your intent examples for semantic overlaps and preview the classifier prompt.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Intent Validation Section */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">Intent Validation</Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleValidateIntents}
                        disabled={validateIntents.isPending}
                      >
                        {validateIntents.isPending ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Validating...
                          </>
                        ) : validationRun ? (
                          'Re-validate'
                        ) : (
                          'Validate Intents'
                        )}
                      </Button>
                    </div>
                    
                    {/* Validation Results */}
                    {validationRun && (
                      <div className="space-y-2">
                        {conflicts.length === 0 ? (
                          <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm p-2 bg-green-50 dark:bg-green-950/20 rounded-md">
                            <CheckCircle className="h-4 w-4" />
                            <span>No conflicts found - all intent examples are distinct</span>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {conflicts.map((conflict, idx) => (
                              <div key={idx} className="p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-md">
                                <div className="flex items-start gap-2">
                                  <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                                  <div className="flex-1 space-y-2">
                                    <p className="text-sm text-red-700 dark:text-red-300">
                                      <span className="font-medium">Overlap:</span>{' '}
                                      <code className="bg-red-100 dark:bg-red-900/50 px-1 rounded">"{conflict.example1}"</code>
                                      {' '}({conflict.route1_name})
                                      {' '}↔{' '}
                                      <code className="bg-red-100 dark:bg-red-900/50 px-1 rounded">"{conflict.example2}"</code>
                                      {' '}({conflict.route2_name})
                                    </p>
                                    <p className="text-xs text-red-600 dark:text-red-400">
                                      <span className="font-medium">Reason:</span> {conflict.reason}
                                    </p>
                                    {/* Remove buttons commented out - let user decide and re-validate
                                    <div className="flex gap-2 pt-1">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-6 text-xs"
                                        onClick={() => handleRemoveExample(conflict.route1_id, conflict.example1)}
                                      >
                                        Remove from {conflict.route1_name}
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-6 text-xs"
                                        onClick={() => handleRemoveExample(conflict.route2_id, conflict.example2)}
                                      >
                                        Remove from {conflict.route2_name}
                                      </Button>
                                    </div>
                                    */}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <Separator />
                  
                  {/* Prompt Preview Section */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">Classifier Prompt Preview</Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => {
                          navigator.clipboard.writeText(generatePromptPreview())
                          toast({
                            title: 'Copied',
                            description: 'Intent classifier prompt copied to clipboard',
                          })
                        }}
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        Copy
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      This is the prompt sent to the LLM (gpt-4o-mini) for intent classification.
                    </p>
                    <pre className="text-xs bg-slate-900 text-slate-100 p-4 rounded-md overflow-x-auto whitespace-pre-wrap font-mono">
                      {generatePromptPreview()}
                    </pre>
                  </div>
                </CardContent>
              </Card>
            </CollapsibleContent>
          </Collapsible>
        </>
      )}
    </div>
  )
}
